import { Injectable } from "@angular/core";
import { CanDeactivate, Router, NavigationEnd } from "@angular/router";
import { Observable } from "rxjs";
import { filter } from "rxjs/operators";
import { FormDataPersistenceService } from "../services/form-data-persistence.service";

export interface CanComponentDeactivate {
  canDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
  hasUnsavedChanges?: () => boolean;
  saveFormData?: () => void;
  getFormId?: () => string;
}

@Injectable({
  providedIn: "root",
})
export class EnhancedCanDeactivateGuard
  implements CanDeactivate<CanComponentDeactivate>
{
  private isNavigatingAway = false;

  constructor(
    private router: Router,
    private formPersistenceService: FormDataPersistenceService
  ) {
    // Track navigation events
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.isNavigatingAway = false;
    });
  }

  canDeactivate(
    component: CanComponentDeactivate
  ): Observable<boolean> | Promise<boolean> | boolean {
    // Check if this is a navigation away from the form
    const hasUnsavedChanges = component.hasUnsavedChanges 
      ? component.hasUnsavedChanges() 
      : true;
    
    if (hasUnsavedChanges && component.canDeactivate) {
      // Save form data before navigation if possible
      if (component.saveFormData) {
        component.saveFormData();
      }
      
      // Mark that we're navigating away
      this.isNavigatingAway = true;
      
      // Show confirmation dialog
      const result = component.canDeactivate();
      
      if (typeof result === 'boolean') {
        if (!result && component.saveFormData) {
          // If user cancels navigation, restore form data
          setTimeout(() => {
            component.saveFormData?.();
          }, 100);
        }
        return result;
      }
      
      return result;
    }
    
    // Clear form data if navigation is confirmed and no unsaved changes
    if (!hasUnsavedChanges && component.getFormId) {
      this.formPersistenceService.clearFormData(component.getFormId());
    }
    
    return true;
  }

  /**
   * Check if currently navigating away
   */
  isNavigating(): boolean {
    return this.isNavigatingAway;
  }
}